import React from "react";
import { moduleApi } from "../../lib/api";
import type { BoardColumn } from "./boardColumns";

export interface ParsedReservationRow {
  lastName: string; firstName: string; phone: string; email: string; time: string;
  adults: number; tableNumber: string; comment: string; sourceRef: string;
  /** Valeurs des colonnes personnalisées (existantes ou nouvellement créées), indexées par id de colonne. */
  customValues: Record<string, string>;
}
export interface ReservationImportResult { imported: number; duplicates_skipped: number; invalid_skipped: number }

/**
 * Import de réservations depuis un CSV/Excel externe (Zenchef ou autre plateforme) — même patron que
 * useConvivesActions.importRows, mais côté Service d'exception : la ligne part avec source = 'zenchef'
 * (vocabulaire déjà utilisé par ReservationSourcesPanel.tsx pour ce même point bleu "importée"). Si le
 * mapping a proposé de nouvelles colonnes, elles sont créées sur le tableau AVANT l'envoi des lignes,
 * sinon la grille ne saurait pas encore où afficher les valeurs qu'elle vient de recevoir.
 *
 * BUG CORRIGÉ (2026-09-25, Brasserie Polpo) : `onCreateColumns` (= `onUpdateColumns` du tableau) ATTEND
 * la liste COMPLÈTE des colonnes à enregistrer — c'est ce que fait BoardColumnsModal. Ce hook lui
 * envoyait pourtant seulement `nouvellesColonnes`, le delta créé par le mapping : le premier import avec
 * une colonne inconnue remplaçait donc TOUTES les colonnes existantes (CB, Espèces, Commentaire...) par
 * les seules nouvelles. Aucune valeur n'était perdue en base — seule la définition d'affichage du tableau
 * disparaissait. `boardColumns` (l'état actuel du tableau) doit maintenant être fusionné avant l'envoi.
 */
export function useReservationImport(
  boardId: number | undefined,
  boardColumns: BoardColumn[],
  onCreateColumns: (columns: BoardColumn[]) => Promise<void>,
  onImported: () => void,
) {
  const [importing, setImporting] = React.useState(false);

  const importRows = async (rows: ParsedReservationRow[], nouvellesColonnes: BoardColumn[]): Promise<ReservationImportResult> => {
    if (!boardId) return { imported: 0, duplicates_skipped: 0, invalid_skipped: rows.length };
    setImporting(true);
    try {
      if (nouvellesColonnes.length > 0) await onCreateColumns([...boardColumns, ...nouvellesColonnes]);
      const res: any = await moduleApi.importExceptionServiceReservations(boardId, rows, 'zenchef');
      onImported();
      return { imported: res?.imported || 0, duplicates_skipped: res?.duplicates_skipped || 0, invalid_skipped: res?.invalid_skipped || 0 };
    } finally {
      setImporting(false);
    }
  };

  return { importing, importRows };
}
